import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../components/AppIcon";
import { cn } from "../../utils/cn";
import ModuleBrochure, {
  type BrochureFeature,
  type BrochureMetric,
  type BrochureUseCase,
} from "./ModuleBrochure";

/**
 * Leaves brochure — the second reference build of the Phase 6 template.
 *
 * Tone: "the email chain is over". Pitch lands on the HR lead who closes
 * year-end balances for 80+ people and the employee who needs a statement
 * for their embassy appointment by Friday.
 *
 * Illustration: a synthetic 5-row "month at a glance" calendar with three
 * leaves blocked out (Annual, Sick, Compassionate) and a floating "+" FAB
 * pinned to the trailing edge. Pure SVG-free Tailwind grid composition.
 *
 * Mockup: a "My Leaves" page — three leave entries with status pills,
 * date ranges, and a balance widget in the corner. Tokens mirror the real
 * app's list patterns.
 */

// ----- Illustration: month calendar with leaves blocked out + FAB -----

const LeavesIllustration: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";

  // 5 weeks × 7 days = 35 cells. We highlight a few to suggest leaves.
  // Index map (Mon=0..Sun=6):
  //   row 1 (8-14):  Tue–Thu = Annual (5 days actually need wk2 too)
  //   row 2 (15-21): Mon–Fri continuation of Annual
  //   row 3 (22-28): Wed = Sick (1 day)
  //   row 4 (29-31): Mon–Tue = Compassionate (2 days)
  const LEAVE_TYPE: Array<"annual" | "sick" | "compassionate" | null> = Array(35).fill(null);
  // Annual block (5 days across week boundary)
  LEAVE_TYPE[8] = "annual";
  LEAVE_TYPE[9] = "annual";
  LEAVE_TYPE[10] = "annual";
  LEAVE_TYPE[11] = "annual";
  LEAVE_TYPE[12] = "annual";
  // Sick
  LEAVE_TYPE[17] = "sick";
  // Compassionate
  LEAVE_TYPE[24] = "compassionate";
  LEAVE_TYPE[25] = "compassionate";

  const TYPE_STYLES = {
    annual: "bg-amber-200 text-amber-900 ring-amber-300",
    sick: "bg-rose-200 text-rose-900 ring-rose-300",
    compassionate: "bg-violet-200 text-violet-900 ring-violet-300",
  } as const;

  return (
    <div className="relative aspect-[4/5] w-full">
      <div className="absolute inset-0 rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.25)]">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">
              {t("brochure.leaves.illustration.month")}
            </p>
            <p className="mt-0.5 text-base font-semibold text-slate-900">
              {t("brochure.leaves.illustration.monthName")}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-slate-200 text-slate-400">
              <Icon name="ChevronLeft" size={12} className={isRtl ? "rotate-180" : ""} />
            </span>
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-slate-200 text-slate-400">
              <Icon name="ChevronRight" size={12} className={isRtl ? "rotate-180" : ""} />
            </span>
          </div>
        </div>

        {/* Day-of-week row */}
        <div className="mt-4 grid grid-cols-7 gap-1 text-center text-[9px] font-semibold uppercase tracking-wider text-slate-400">
          {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
            <span key={`${d}-${i}`}>{d}</span>
          ))}
        </div>

        {/* Calendar grid — 35 cells */}
        <div className="mt-1 grid grid-cols-7 gap-1">
          {LEAVE_TYPE.map((leave, i) => {
            const dayNum = i + 1;
            const isPast = dayNum < 7;
            const baseDigit = ((dayNum - 1) % 31) + 1;
            return (
              <div
                key={i}
                className={cn(
                  "aspect-square rounded-md text-center text-[10px] tabular-nums",
                  "flex items-center justify-center",
                  leave
                    ? cn("font-semibold ring-1", TYPE_STYLES[leave])
                    : isPast
                      ? "text-slate-300"
                      : "text-slate-500",
                )}
              >
                {baseDigit}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap items-center gap-2 text-[9px] font-semibold text-slate-500">
          <span className="inline-flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
            {t("brochure.leaves.illustration.legend.annual")}
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-rose-400" />
            {t("brochure.leaves.illustration.legend.sick")}
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-violet-400" />
            {t("brochure.leaves.illustration.legend.compassionate")}
          </span>
        </div>
      </div>

      {/* Floating "+" FAB at the trailing-bottom corner */}
      <button
        type="button"
        disabled
        aria-hidden="true"
        className={cn(
          "absolute bottom-0 flex h-14 w-14 cursor-default items-center justify-center rounded-full",
          "bg-amber-600 text-white shadow-[0_18px_36px_-10px_rgba(180,83,9,0.55)]",
          "translate-y-1/3",
          isRtl ? "left-3 -translate-x-1/4" : "right-3 translate-x-1/4",
        )}
      >
        <Icon name="Plus" size={22} />
      </button>
    </div>
  );
};

// ----- Mockup: "My Leaves" page -----

interface LeaveRow {
  type: string;
  typeKey: string;
  icon: "Sun" | "Stethoscope" | "Heart";
  iconTint: string;
  range: string;
  rangeKey: string;
  days: number;
  status: "approved" | "pending";
}

const LEAVE_ROWS: LeaveRow[] = [
  {
    type: "Annual",
    typeKey: "brochure.leaves.mockup.type.annual",
    icon: "Sun",
    iconTint: "bg-amber-50 text-amber-700",
    range: "10 – 14 March",
    rangeKey: "brochure.leaves.mockup.range.annual",
    days: 5,
    status: "approved",
  },
  {
    type: "Sick",
    typeKey: "brochure.leaves.mockup.type.sick",
    icon: "Stethoscope",
    iconTint: "bg-rose-50 text-rose-700",
    range: "22 March",
    rangeKey: "brochure.leaves.mockup.range.sick",
    days: 1,
    status: "pending",
  },
  {
    type: "Compassionate",
    typeKey: "brochure.leaves.mockup.type.compassionate",
    icon: "Heart",
    iconTint: "bg-violet-50 text-violet-700",
    range: "29 – 30 March",
    rangeKey: "brochure.leaves.mockup.range.compassionate",
    days: 2,
    status: "approved",
  },
];

const LeavesMockup: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_220px]">
      <div className="overflow-hidden rounded-lg border border-slate-200">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <Icon name="Calendar" size={14} className="text-slate-500" aria-hidden="true" />
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
              {t("brochure.leaves.mockup.heading")}
            </p>
          </div>
          <p className="text-[10px] font-medium text-slate-400">
            {t("brochure.leaves.mockup.thisYear")}
          </p>
        </div>

        <ul className="divide-y divide-slate-100 bg-white">
          {LEAVE_ROWS.map((r, i) => (
            <li key={i} className="flex items-center gap-3 px-4 py-3">
              <span
                className={cn(
                  "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                  r.iconTint,
                )}
              >
                <Icon name={r.icon} size={16} aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-slate-900">{t(r.typeKey)}</p>
                <p className="text-[10px] text-slate-500 tabular-nums">{t(r.rangeKey)}</p>
              </div>
              <div className="hidden text-end sm:block">
                <p className="text-xs font-semibold tabular-nums text-slate-900">
                  {t("brochure.leaves.mockup.daysLabel", { count: r.days })}
                </p>
              </div>
              <span
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1",
                  r.status === "approved"
                    ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                    : "bg-amber-50 text-amber-800 ring-amber-200",
                )}
              >
                <span
                  className={cn(
                    "h-1.5 w-1.5 rounded-full",
                    r.status === "approved" ? "bg-emerald-500" : "bg-amber-500",
                  )}
                />
                {r.status === "approved"
                  ? t("brochure.leaves.mockup.status.approved")
                  : t("brochure.leaves.mockup.status.pending")}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Balance widget */}
      <div className="grid gap-3">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            {t("brochure.leaves.mockup.balance.heading")}
          </p>
          <div className="mt-2 flex items-baseline gap-1">
            <p className="text-2xl font-semibold tabular-nums text-slate-900">14.5</p>
            <p className="text-xs text-slate-500">
              / <span className="tabular-nums">21</span> {t("brochure.leaves.mockup.balance.unit")}
            </p>
          </div>
          {/* Progress bar */}
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-amber-500"
              style={{ width: "69%" }}
              aria-hidden="true"
            />
          </div>
          <p className="mt-2 text-[10px] leading-snug text-slate-500">
            {t("brochure.leaves.mockup.balance.hint")}
          </p>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-800">
            {t("brochure.leaves.mockup.statement.heading")}
          </p>
          <p className="mt-1.5 text-xs leading-snug text-amber-900/80">
            {t("brochure.leaves.mockup.statement.hint")}
          </p>
          <span className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-900">
            <Icon name="Download" size={12} aria-hidden="true" />
            {t("brochure.leaves.mockup.statement.cta")}
          </span>
        </div>
      </div>
    </div>
  );
};

// ----- Brochure props ------

const LeavesBrochure: React.FC = () => {
  const { t } = useTranslation();

  const features: BrochureFeature[] = [
    {
      icon: "Send",
      title: t("brochure.leaves.features.selfService.title"),
      description: t("brochure.leaves.features.selfService.description"),
    },
    {
      icon: "GitBranch",
      title: t("brochure.leaves.features.approvals.title"),
      description: t("brochure.leaves.features.approvals.description"),
    },
    {
      icon: "ShieldCheck",
      title: t("brochure.leaves.features.statutory.title"),
      description: t("brochure.leaves.features.statutory.description"),
    },
    {
      icon: "TrendingUp",
      title: t("brochure.leaves.features.accrual.title"),
      description: t("brochure.leaves.features.accrual.description"),
    },
    {
      icon: "Users",
      title: t("brochure.leaves.features.teamCalendar.title"),
      description: t("brochure.leaves.features.teamCalendar.description"),
    },
    {
      icon: "FileText",
      title: t("brochure.leaves.features.statements.title"),
      description: t("brochure.leaves.features.statements.description"),
    },
  ];

  const metrics: BrochureMetric[] = [
    {
      value: t("brochure.leaves.metrics.speedValue"),
      label: t("brochure.leaves.metrics.speedLabel"),
    },
    {
      value: "100%",
      label: t("brochure.leaves.metrics.complianceLabel"),
    },
    {
      value: t("brochure.leaves.metrics.statementValue"),
      label: t("brochure.leaves.metrics.statementLabel"),
    },
  ];

  const useCases: BrochureUseCase[] = [
    {
      persona: t("brochure.leaves.useCases.employee.persona"),
      scenario: t("brochure.leaves.useCases.employee.scenario"),
      outcome: t("brochure.leaves.useCases.employee.outcome"),
    },
    {
      persona: t("brochure.leaves.useCases.hr.persona"),
      scenario: t("brochure.leaves.useCases.hr.scenario"),
      outcome: t("brochure.leaves.useCases.hr.outcome"),
    },
  ];

  return (
    <ModuleBrochure
      slug="leaves"
      hero={{
        eyebrow: t("brochure.leaves.hero.eyebrow"),
        headline: t("brochure.leaves.hero.headline"),
        subheadline: t("brochure.leaves.hero.subheadline"),
        illustration: <LeavesIllustration />,
      }}
      features={features}
      metrics={metrics}
      mockup={{
        title: t("brochure.leaves.mockup.caption"),
        content: <LeavesMockup />,
      }}
      useCases={useCases}
    />
  );
};

export default LeavesBrochure;
